import { useState } from 'react';
import { CAR_PARTS, DAMAGE_TYPES, type CarPart, type DamageType } from '@adbonus/core';
import { useI18n } from '../../lib/i18n';
import type { ShrunkPhoto } from '../../lib/photo';
import { Sheet } from '../Sheet';
import { Button, Field, Label, Select } from '../ui';
import { useInspectionWords } from './labels';

export interface DamageDetails {
  damageType: DamageType;
  part: CarPart | null;
  note: string | null;
}

/**
 * Describing a mark just photographed: what it is (one tap), where it is,
 * and a word if needed. Only "what" is required — the photo shows the rest.
 */
export function DamageForm({ photo, onSave, onCancel }: {
  photo: ShrunkPhoto;
  onSave: (details: DamageDetails) => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const words = useInspectionWords();
  const [damageType, setDamageType] = useState<DamageType | null>(null);
  const [part, setPart] = useState<CarPart | ''>('');
  const [note, setNote] = useState('');

  return (
    <Sheet
      open
      onClose={onCancel}
      title={t('inspection.addDamage')}
      footer={(
        <Button
          size="lg"
          className="w-full"
          disabled={!damageType}
          onClick={() => damageType && onSave({ damageType, part: part || null, note: note.trim() || null })}
        >
          {t('inspection.damageSave')}
        </Button>
      )}
    >
      <img
        src={photo.previewUrl}
        alt=""
        className="mx-auto max-h-56 w-full rounded-xl border border-line object-contain"
      />

      <div className="mt-4">
        <Label>{t('inspection.damageWhat')}</Label>
        <div className="mt-1.5 grid grid-cols-3 gap-2">
          {DAMAGE_TYPES.map((type) => (
            <button
              key={type}
              onClick={() => setDamageType(type)}
              aria-pressed={damageType === type}
              className={`min-h-11 rounded-xl border px-2 text-sm font-medium transition-colors ${
                damageType === type
                  ? 'border-warning bg-warning/10 text-warning'
                  : 'border-line bg-raised text-ink-dim hover:border-ink-faint hover:text-ink'
              }`}
            >
              {words.damage(type)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <Select
          label={t('inspection.damageWhere')}
          value={part}
          onChange={(event) => setPart(event.target.value as CarPart | '')}
        >
          <option value="">—</option>
          {CAR_PARTS.map((option) => (
            <option key={option} value={option}>{words.part(option)}</option>
          ))}
        </Select>
        <Field
          label={t('inspection.damageNote')}
          value={note}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
    </Sheet>
  );
}
